import { Entities } from 'constants/Entities';
import { LIST_GLOBAL_MULTI_ENVIRONMENT_SKIP } from 'constants/ListGlobalMultiEnvironmentSkip';
import {
    getSelectedMultiEnvironmentIds,
    isMultiEnvironmentModeActive,
} from 'utils/multiEnvironment';

/**
 * @param {Object} state - Redux state
 * @param {string} [filtersKey] - `filterKey || entity.entity`
 * @param {typeof LIST_GLOBAL_MULTI_ENVIRONMENT_SKIP} [skipConfig]
 * @returns {boolean}
 */
/**
 * Scoped recipient-selection lists must not persist filters to session/cache
 * (shared filterKey across campaigns would otherwise leak state).
 *
 * @param {string} [filterKey]
 * @returns {boolean}
 */
export function shouldSkipFilterPersistence(
    filterKey,
    skipConfig = LIST_GLOBAL_MULTI_ENVIRONMENT_SKIP,
) {
    return !!filterKey && skipConfig.filterKeys.includes(filterKey);
}

export function shouldSkipListGlobalMultiEnvironmentFilter(
    state,
    filtersKey,
    skipConfig = LIST_GLOBAL_MULTI_ENVIRONMENT_SKIP,
) {
    if (filtersKey && skipConfig.filterKeys.includes(filtersKey)) {
        return true;
    }

    const entityShowSelected =
        filtersKey && state.entityFilters?.[filtersKey]?.filters?.entityShowSelected?.value;

    return skipConfig.entityShowSelectedValues.includes(entityShowSelected);
}

/**
 * Inject sidebar multi-environment filter into raw entity-list filters when applicable.
 *
 * @param {Object} state - Redux state
 * @param {string} entityName - Entity key from Entities config
 * @param {Object} baseFilters - Raw filters from entityFilters slice
 * @param {string} [filtersKey] - Namespace key (`filterKey || entity.entity`)
 * @returns {Object}
 */
export function withListGlobalMultiEnvironmentFilters(state, entityName, baseFilters, filtersKey) {
    if (shouldSkipListGlobalMultiEnvironmentFilter(state, filtersKey)) {
        return baseFilters;
    }

    if (!isMultiEnvironmentModeActive(state)) {
        return baseFilters;
    }

    const environmentFilterField = Entities[entityName]?.environmentFilterField;

    if (!environmentFilterField) {
        return baseFilters;
    }

    const selectedEnvironmentIds = getSelectedMultiEnvironmentIds(state);
    const formattedValue = selectedEnvironmentIds.map((id) => ({ value: id }));

    return {
        ...baseFilters,
        [environmentFilterField]: {
            id: environmentFilterField,
            dataType: 'singleValueList',
            value: formattedValue,
            completeValues: formattedValue,
        },
    };
}
